import { supabase } from './auth.service';
import { invokeEdgeFunction } from '../lib/edge-function';
import { Channel } from '../entities/channel.entity';
import { mapUser } from '../lib/map-user';

export interface ConnectWhatsAppCredentials {
  phoneNumberId: string;
  accessToken: string;
  businessAccountId: string;
  name?: string;
}

export class ChannelService {
  constructor() {}

  async loadAll(): Promise<Channel[]> {
    const { data, error } = await supabase
      .from('channels')
      .select('*, createdBy:users!created_by(*)')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data.map(this.mapToEntity);
  }

  async softDelete(id: string): Promise<void> {
    await invokeEdgeFunction('channel-delete', {
      body: { channelId: id },
    });
  }

  async create(
    channel: Omit<Channel, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>
  ): Promise<Channel> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('channels')
      .insert({
        account_id: channel.accountId,
        network: channel.network,
        name: channel.name,
        network_id: channel.networkId,
        image_url: channel.imageUrl || null,
        is_active: channel.isActive,
        access_token: channel.accessToken,
        refresh_token: channel.refreshToken,
        token_expires_at: channel.tokenExpiresAt,
        configuration: channel.configuration,
        created_by: user.id,
      })
      .select('*, createdBy:users!created_by(*)')
      .single();

    if (error) throw error;
    return this.mapToEntity(data);
  }

  async updateById(id: string, channel: Partial<Channel>): Promise<Channel> {
    const { data, error } = await supabase
      .from('channels')
      .update({
        network: channel.network,
        name: channel.name,
        network_id: channel.networkId,
        image_url: channel.imageUrl || null,
        is_active: channel.isActive,
        access_token: channel.accessToken,
        refresh_token: channel.refreshToken,
        token_expires_at: channel.tokenExpiresAt,
        configuration: channel.configuration,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select('*, createdBy:users!created_by(*)')
      .single();

    if (error) throw error;
    return this.mapToEntity(data);
  }

  async connectWhatsApp(credentials: ConnectWhatsAppCredentials): Promise<Channel> {
    return invokeEdgeFunction<Channel>('whatsapp-connect', {
      body: credentials,
    });
  }

  async disconnectWhatsApp(channelId: string): Promise<void> {
    await invokeEdgeFunction('whatsapp-disconnect', {
      body: { channelId },
    });
  }

  async getWhatsAppStatus(channelId: string): Promise<{
    connected: boolean;
    phoneNumberVerified: boolean;
    lastWebhookAt?: string;
  }> {
    return invokeEdgeFunction<{
      connected: boolean;
      phoneNumberVerified: boolean;
      lastWebhookAt?: string;
    }>('whatsapp-status', {
      body: { channelId },
    });
  }

  async testWhatsAppConnection(credentials: ConnectWhatsAppCredentials): Promise<{
    success: boolean;
    message: string;
    phoneNumberInfo?: {
      displayName: string;
      phoneNumber: string;
      verifiedName: string;
    };
  }> {
    const data = await invokeEdgeFunction<{
      success: boolean;
      message: string;
      phoneNumberInfo?: {
        displayName: string;
        phoneNumber: string;
        verifiedName: string;
      };
    }>('whatsapp-test', {
      body: credentials,
    });
    return data;
  }

  async initiateWhatsAppOAuth(): Promise<string> {
    const data = await invokeEdgeFunction<{ data: { redirectUrl: string } }>('whatsapp-connect', {
      headers: { 'x-path': '/oauth' },
    });
    return data.data.redirectUrl;
  }

  async getWhatsAppPhoneNumbers(encryptedToken: string): Promise<{
    wabaId: string;
    phoneNumbers: unknown[];
  }> {
    const data = await invokeEdgeFunction<{ data: { wabaId: string; phoneNumbers: unknown[] } }>('whatsapp-connect', {
      headers: { 'x-path': '/get-phone-numbers', 'x-encrypted-token': encryptedToken },
    });
    return data.data;
  }

  async createWhatsAppChannel(
    phoneNumberId: string,
    wabaId: string,
    name: string | undefined,
    encryptedToken: string
  ): Promise<Channel> {
    const data = await invokeEdgeFunction<{ data: any }>('whatsapp-connect', {
      headers: { 'x-path': '/create-channel' },
      body: { phoneNumberId, wabaId, name, encryptedToken },
    });
    return this.mapToEntity(data.data);
  }

  async initiateFacebookOAuth(): Promise<string> {
    const data = await invokeEdgeFunction<{ data: { redirectUrl: string } }>('facebook-connect', {
      headers: { 'x-path': '/oauth' },
    });
    return data.data.redirectUrl;
  }

  async getWhatsAppTemplates(channelId: string): Promise<any[]> {
    const data = await invokeEdgeFunction<{ data: any[] }>('whatsapp-templates', {
      body: { action: 'list', channelId },
    });
    return data.data || [];
  }

  async createWhatsAppTemplate(channelId: string, templateData: any): Promise<any> {
    const data = await invokeEdgeFunction<{ data: any }>('whatsapp-templates', {
      body: { action: 'create', channelId, templateData },
    });
    return data.data;
  }

  async deleteWhatsAppTemplate(channelId: string, templateId: string): Promise<void> {
    await invokeEdgeFunction('whatsapp-templates', {
      body: { action: 'delete', channelId, templateData: { id: templateId } },
    });
  }

  async getFacebookPages(encryptedToken: string): Promise<unknown[]> {
    const data = await invokeEdgeFunction<{ data: unknown[] }>('facebook-connect', {
      headers: { 'x-path': '/get-pages', 'x-encrypted-token': encryptedToken },
    });
    return data.data || [];
  }

  async createFacebookPages(
    pages: unknown[],
    encryptedToken: string
  ): Promise<{ channels: Channel[]; blocked: { id: string; name: string; reason: string }[] }> {
    const data = await invokeEdgeFunction<{
      data: {
        channels: any[];
        blocked: { id: string; name: string; reason: string }[];
      };
    }>('facebook-connect', {
      headers: { 'x-path': '/create-pages' },
      body: { pages, encryptedToken },
    });
    return {
      channels: (data.data.channels || []).map((r: any) => this.mapToEntity(r)),
      blocked: data.data.blocked || [],
    };
  }

  async initiateInstagramOAuth(): Promise<string> {
    const data = await invokeEdgeFunction<{ data: { redirectUrl: string } }>('instagram-connect', {
      headers: { 'x-path': '/oauth' },
    });
    return data.data.redirectUrl;
  }

  private mapToEntity(row: any): Channel {
    return {
      id: row.id,
      accountId: row.account_id,
      network: row.network,
      name: row.name,
      networkId: row.network_id,
      imageUrl: row.image_url || undefined,
      isActive: row.is_active,
      accessToken: row.access_token,
      refreshToken: row.refresh_token,
      tokenExpiresAt: row.token_expires_at,
      configuration: row.configuration || row.config,
      webhookSecret: row.webhook_secret,
      webhookVerified: row.webhook_verified,
      lastPulledAt: row.last_pulled_at,
      refreshTokenExpiresAt: row.refresh_token_expires_at,
      lastMessagesPulledAt: row.last_messages_pulled_at,
      pendingSync: row.pending_sync,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
      createdBy: mapUser(row.createdBy, row.account_id),
    };
  }
}

export const channelService = new ChannelService();
